import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, Card, Badge, Row, Col } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import ImageInput from '../../common/FormComponents/ImageInput';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const TrustFactorsManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
  });

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('Trust factor deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete trust factor');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(section.sectionKey, itemId, payload);
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      console.log('=== SUBMIT DEBUG ===');
      console.log('All form values:', JSON.stringify(values, null, 2));
      console.log('values.icon:', values.icon);
      console.log('values.icon type:', typeof values.icon);
      console.log('values.icon is array:', Array.isArray(values.icon));
      if (values.icon && values.icon.length > 0) {
        console.log('values.icon[0]:', values.icon[0]);
        console.log('values.icon[0].imgData:', values.icon[0]?.imgData);
        console.log('Is File?:', values.icon[0]?.imgData instanceof File);
      }

      const payload = {
        title: values.title,
        description: values.description,
        imageType: 'icon',
      };

      if (values.icon?.[0]?.imgData && values.icon[0].imgData instanceof File) {
        payload.file = values.icon[0].imgData;
        console.log('✓ File added to payload:', payload.file.name, payload.file.size, 'bytes');
      } else if (editingItem?.icon) {
        payload.icon = editingItem.icon;
        console.log('✓ Using existing icon');
      } else {
        console.log('✗ NO FILE FOUND in values.icon');
      }

      console.log('Payload:', payload);
      const formData = getFormDataFromObject(payload);
      console.log('FormData created, entries:');
      for (let pair of formData.entries()) {
        console.log('  -', pair[0], ':', pair[1]);
      }

      if (editingItem) {
        await homepageApi.updateItemInSection(section.sectionKey, editingItem._id, formData);
        toastSuccess('Trust factor updated successfully');
      } else {
        await homepageApi.addItemToSection(section.sectionKey, formData);
        toastSuccess('Trust factor added successfully');
      }

      refreshSections();
      fetchItems();
      setShowModal(false);
      resetForm();
    } catch (error) {
      toastError(editingItem ? 'Failed to update trust factor' : 'Failed to add trust factor');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, { items: updatedItems });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems();
    }
  };

  if (loading && !items.length) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                Landing Page CMS
              </li>
              <li>Trust Factors Management</li>
            </ol>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">
                      <i className="fas fa-shield-alt me-2 text-primary"></i>
                      Trust Factors Management
                    </h3>
                    <p className="text-muted mb-0">Build credibility with trust factors displayed on your homepage</p>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button variant="success" onClick={handleAdd} disabled={loading}>
                      <i className="fas fa-plus me-2"></i>Add New Factor
                    </Button>
                    <Button variant="secondary" onClick={backToList}>
                      <i className="fas fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </div>
              </div>

              <div className="middle_main">
                {sortedItems.length === 0 ? (
                  <Card className="text-center py-5 border-0 bg-light">
                    <Card.Body>
                      <i className="fas fa-shield-alt fa-5x text-muted mb-4 d-block"></i>
                      <h4 className="text-muted mb-2">No Trust Factors Yet</h4>
                      <p className="text-muted mb-4">Start building credibility by adding your first trust factor</p>
                      <Button variant="primary" size="lg" onClick={handleAdd}>
                        <i className="fas fa-plus me-2"></i>Add Your First Factor
                      </Button>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <Card className="mb-4 bg-light border-0">
                      <Card.Body className="py-3">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-info-circle text-primary me-3 fs-4"></i>
                          <div>
                            <strong className="d-block mb-1">Quick Tips:</strong>
                            <small className="text-muted">
                              • Drag cards to reorder • Toggle switch to show/hide • Keep descriptions concise (1-2 sentences)
                            </small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>

                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="trust_factors">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="row"
                            style={{
                              backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'transparent',
                              transition: 'background-color 0.2s ease',
                              padding: '10px',
                              borderRadius: '8px',
                            }}
                          >
                            {sortedItems.map((item, index) => (
                              <Draggable key={item._id} draggableId={item._id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    className="col-lg-6 col-md-12 mb-4"
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <Card
                                      className="h-100 shadow-sm border"
                                      style={{
                                        backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'white',
                                        transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                                        transition: 'all 0.2s ease',
                                      }}
                                    >
                                      <Card.Header className="bg-light border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                              <i className="fas fa-grip-vertical text-muted"></i>
                                            </div>
                                            <Badge bg="primary" pill>#{index + 1}</Badge>
                                            <strong className="text-dark">{item.title}</strong>
                                          </div>
                                          <ReactSwitch
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            onChange={() => handleToggleStatus(item._id, item.status)}
                                            checked={item.status}
                                            height={20}
                                            width={40}
                                            onColor="#28a745"
                                            offColor="#6c757d"
                                          />
                                        </div>
                                      </Card.Header>

                                      <Card.Body>
                                        <Row>
                                          <Col md={3} className="text-center mb-3 mb-md-0">
                                            {item.icon?.location ? (
                                              <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                  width: '80px',
                                                  height: '80px',
                                                  backgroundColor: '#f8f9fa',
                                                  borderRadius: '12px',
                                                  margin: '0 auto',
                                                }}
                                              >
                                                <img
                                                  src={item.icon.location}
                                                  alt={item.title}
                                                  style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                                />
                                              </div>
                                            ) : (
                                              <div
                                                className="d-flex align-items-center justify-content-center text-muted"
                                                style={{
                                                  width: '80px',
                                                  height: '80px',
                                                  backgroundColor: '#f8f9fa',
                                                  borderRadius: '12px',
                                                  margin: '0 auto',
                                                }}
                                              >
                                                <i className="fas fa-image fa-2x"></i>
                                              </div>
                                            )}
                                            <div className="mt-2">
                                              {item.status ? (
                                                <Badge bg="success" className="w-100">
                                                  <i className="fas fa-check-circle me-1"></i>Active
                                                </Badge>
                                              ) : (
                                                <Badge bg="secondary" className="w-100">
                                                  <i className="fas fa-eye-slash me-1"></i>Hidden
                                                </Badge>
                                              )}
                                            </div>
                                          </Col>

                                          <Col md={9}>
                                            <div className="mb-3">
                                              <small className="text-muted d-block mb-1">
                                                <i className="fas fa-align-left me-1"></i>Description
                                              </small>
                                              <p className="mb-0">{item.description}</p>
                                            </div>

                                            <div className="d-flex gap-2 mt-3">
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleEdit(item)}
                                                className="flex-fill"
                                              >
                                                <i className="fas fa-edit me-1"></i>Edit
                                              </Button>
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(item._id)}
                                                className="flex-fill"
                                              >
                                                <i className="fas fa-trash me-1"></i>Delete
                                              </Button>
                                            </div>
                                          </Col>
                                        </Row>
                                      </Card.Body>
                                    </Card>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i className={`fas fa-${editingItem ? 'edit' : 'plus'} me-2 text-primary`}></i>
            {editingItem ? 'Edit' : 'Add'} Trust Factor
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            title: editingItem?.title || '',
            description: editingItem?.description || '',
            icon: editingItem?.icon ? [{ PrevUrl: editingItem.icon.location }] : [],
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form>
              <Modal.Body>
                <Card className="mb-3 border-0 bg-light">
                  <Card.Body className="py-2">
                    <small className="text-muted">
                      <i className="fas fa-lightbulb text-warning me-2"></i>
                      Create a compelling trust factor that highlights your credibility and expertise
                    </small>
                  </Card.Body>
                </Card>

                <div className="row">
                  <div className="col-md-12 form-group mb-3">
                    <label className="form-label">
                      <i className="fas fa-heading me-2 text-primary"></i>
                      Title <span className="text-danger">*</span>
                    </label>
                    <CustomInput
                      name="title"
                      placeholder="e.g., Science you can trust"
                      labelclassname="form-label"
                      className="form-control"
                      isRequired
                    />
                    <small className="text-muted">Keep it concise and impactful (3-5 words)</small>
                  </div>

                  <div className="col-md-12 form-group mb-3">
                    <label className="form-label">
                      <i className="fas fa-align-left me-2 text-primary"></i>
                      Description <span className="text-danger">*</span>
                    </label>
                    <CustomInput
                      name="description"
                      placeholder="Every recommendation is backed by proven research and clinical evidence..."
                      labelclassname="form-label"
                      className="form-control"
                      isRequired
                    />
                    <small className="text-muted">Brief explanation (1-2 sentences max)</small>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label">
                      <i className="fas fa-image me-2 text-primary"></i>
                      Icon
                    </label>
                    <ImageInput
                      name="icon"
                      id="trust_icon"
                      accept="image/*"
                      imagesLimit={1}
                    />
                    <small className="text-muted d-block mt-2">
                      <i className="fas fa-info-circle me-1"></i>
                      Recommended: SVG or PNG format. Any size accepted.
                    </small>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className="bg-light">
                <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>Saving...
                    </>
                  ) : (
                    <>
                      <i className={`fas fa-${editingItem ? 'save' : 'plus'} me-2`}></i>
                      {editingItem ? 'Update' : 'Add'} Factor
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <DeleteConfirmationModal
        name="Trust Factor"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="Trust Factors"
      />
    </div>
  );
};

export default TrustFactorsManager;
